import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/lib/useSeo";
import { ArrowLeft, Plus, Minus } from "lucide-react";

const SECTIONS = [
  {
    title: "Come funziona",
    items: [
      {
        q: "Se il club è 'solo su invito', come mi iscrivo se sono tra i primi?",
        a: "La waitlist è tecnicamente aperta a chiunque. La formula 'solo su invito' descrive lo spirito del club — non è un vincolo tecnico. Chiunque può iscriversi con la propria email. Chi arriva tramite un link di invito personale fa scalare la coda al referrer.",
      },
      {
        q: "Cos'è esattamente il co-finanziamento turistico?",
        a: "È un pre-acquisto collettivo di ospitalità. Anticipi una quota simbolica (49€) per il soggiorno futuro in un hotel 5 stelle in ristrutturazione. Se il target di raccolta viene raggiunto, l'hotel usa i fondi per completare i lavori. Al termine dei lavori, ricevi il voucher soggiorno.",
      },
      {
        q: "È un investimento? Rischio i miei soldi?",
        a: "Non è un investimento speculativo. Non promettiamo rendimenti finanziari. Se l'hotel non completa la ristrutturazione, ricevi il 100% della quota indietro. I fondi restano in escrow finché non si concretizza l'avanzamento certificato dei lavori.",
      },
      {
        q: "Come si sblocca il voucher soggiorno?",
        a: "Quando l'hotel completa i lavori e ottiene le certificazioni, tutti i co-finanziatori ricevono via email il voucher digitale. Il voucher garantisce 4 giorni in camera doppia nell'anno solare successivo, utilizzabile nei periodi di bassa e media stagione dell'hotel.",
      },
    ],
  },
  {
    title: "Sicurezza e fondi",
    items: [
      {
        q: "Cos'è il conto escrow?",
        a: "È un conto di garanzia indipendente gestito dal fornitore di pagamenti Stripe. I tuoi soldi non finiscono direttamente né a SuiteCrowd né all'hotel: vengono congelati fino al raggiungimento delle condizioni di svincolo (target raccolta o avanzamento certificato lavori).",
      },
      {
        q: "E se l'hotel non riapre nei tempi previsti?",
        a: "La quota versata viene interamente riaccreditata sul metodo di pagamento originario. Non serve richiesta esplicita: il rimborso è automatico. Zero rischio finanziario per te.",
      },
      {
        q: "Perché Stripe e non un bonifico bancario diretto?",
        a: "Stripe è certificato PCI-DSS Level 1 e ha protocolli di escrow validati a livello europeo. Un bonifico diretto ci renderebbe custodi dei tuoi fondi — invece così Stripe fa da terza parte fiduciaria, garantendo trasparenza completa.",
      },
      {
        q: "Chi vede i miei dati personali?",
        a: "Solo SuiteCrowd e Stripe. Il tuo nome non appare mai pubblicamente: nella Founding Wall e nel Podio l'email è sempre mascherata (es. m***@d***.com). L'hotel non riceve i tuoi dati finché non prenoti il soggiorno.",
      },
    ],
  },
  {
    title: "Founding Member",
    items: [
      {
        q: "Perché diventare Founding Member a 9€?",
        a: "Perché ottieni il tier Platino a vita (upgrade suite garantito, concierge WhatsApp, invito serate private), salti la fila dei 50 inviti necessari a raggiungerlo altrimenti, e il tuo nome resta permanentemente nella Founding Wall pubblica. Solo 100 slot disponibili totali.",
      },
      {
        q: "I 9€ sono rimborsabili?",
        a: "Sì. Se il club non parte entro 12 mesi dal tuo pagamento, ricevi il rimborso automatico dei 9€. È un impegno commerciale che comunichiamo esplicitamente in fase di pagamento Stripe.",
      },
      {
        q: "Cosa succede quando restano gli ultimi 20 slot?",
        a: "Il prezzo sale automaticamente da 9€ a 19€ per i 20 slot finali. Questo per premiare chi crede nel club dal giorno 1. Il salto di prezzo è comunicato via sticky bar e nella sezione Founding.",
      },
      {
        q: "Il certificato Founding è reale o simbolico?",
        a: "Ha valore simbolico (non è titolo finanziario o legale), ma è pubblico e verificabile: ogni certificato ha un URL permanente del tipo suitecrowd.com/certificato/CODICE. Puoi scaricarlo in PDF, condividerlo su LinkedIn/Instagram, o stamparlo.",
      },
    ],
  },
  {
    title: "Voucher e soggiorno",
    items: [
      {
        q: "Quali sono i periodi di bassa e media stagione?",
        a: "Sono definiti da ciascun hotel in base al calendario locale. Tipicamente escludono agosto, capodanno, pasqua e ponti festivi. L'hotel pubblica il calendario dettagliato nella scheda progetto prima dell'apertura prenotazioni.",
      },
      {
        q: "Posso soggiornare in alta stagione?",
        a: "Sì, ma è previsto un supplemento monetario obbligatorio da saldare al momento della prenotazione effettiva. Il supplemento copre la differenza di tariffa e viene comunicato in anticipo dall'hotel.",
      },
      {
        q: "Posso trasferire il voucher a un'altra persona?",
        a: "Il voucher è nominativo e non liberamente cedibile a terzi. Il trasferimento è possibile previa autorizzazione scritta della piattaforma (contattaci a info@suitecrowd.com). Le richieste vengono valutate caso per caso.",
      },
      {
        q: "Posso portare figli, cane, un ospite in più?",
        a: "Il voucher garantisce camera doppia per due adulti. Ospiti aggiuntivi, culle, animali domestici, o servizi extra sono soggetti alle policy standard dell'hotel al momento della prenotazione.",
      },
    ],
  },
  {
    title: "Per gli hotel",
    items: [
      {
        q: "Sono proprietario di un hotel, come lo propongo?",
        a: "Vai su /proponi e crea il tuo account gratuito. Compila la scheda del progetto (foto, descrizione, target di raccolta, quota), invia in revisione. Il team SuiteCrowd valuta ogni proposta manualmente prima della pubblicazione.",
      },
      {
        q: "Chi decide se il mio hotel viene approvato?",
        a: "Il team SuiteCrowd valuta ogni proposta basandosi su: qualità della documentazione, credibilità della ristrutturazione, coerenza col brand luxury del club, sostenibilità economica del target di raccolta.",
      },
      {
        q: "Posso pubblicare aggiornamenti dei lavori?",
        a: "Sì, dal tuo pannello hotelier puoi caricare foto e testi nella sezione 'Avanzamento Lavori' — visibili pubblicamente sulla scheda del tuo hotel. Ti consigliamo un update ogni 2-3 settimane per mantenere alta la fiducia dei co-finanziatori.",
      },
    ],
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(null); // "sectionIdx-itemIdx"

  useSeo({
    title: "Domande Frequenti — SuiteCrowd",
    description:
      "Tutte le risposte su SuiteCrowd: come funziona il co-finanziamento turistico, sicurezza dei fondi in escrow, Founding Member, voucher soggiorno, portale hotelier.",
    canonical: "https://suitecrowd.com/faq",
  });

  const toggle = (key) => setOpen(open === key ? null : key);

  return (
    <div className="min-h-screen bg-white text-[#121212]">
      <nav className="border-b border-[#E5E5E5] bg-white/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex justify-between items-center">
          <Logo variant="dark" size="md" />
          <Link to="/" className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 hover:text-[#121212] inline-flex items-center gap-2 transition-colors">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      <section className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="sc-overline mb-6">Domande Frequenti</div>
        <h1 className="font-display font-light text-4xl md:text-5xl mb-6" style={{ letterSpacing: "-0.03em" }}>
          Tutto quello che vuoi sapere prima di entrare
        </h1>
        <p className="text-neutral-600 leading-relaxed mb-16 max-w-2xl">
          Se non trovi la risposta qui, scrivici a{" "}
          <a href="mailto:info@suitecrowd.com" className="underline decoration-neutral-400 hover:text-[#0B192C]">
            info@suitecrowd.com
          </a>
          . Rispondiamo entro 24h nei giorni lavorativi.
        </p>

        <div className="space-y-16">
          {SECTIONS.map((section, si) => (
            <div key={si}>
              <div className="sc-overline mb-8 text-[#0B192C]">{section.title}</div>
              <div className="divide-y divide-[#E5E5E5] border-t border-b border-[#E5E5E5]">
                {section.items.map((item, ii) => {
                  const key = `${si}-${ii}`;
                  const isOpen = open === key;
                  return (
                    <div key={ii} data-testid={`faq-item-${si}-${ii}`}>
                      <button
                        onClick={() => toggle(key)}
                        className="w-full py-6 flex justify-between items-start gap-6 text-left group"
                      >
                        <span className="font-display text-lg font-normal text-[#121212] group-hover:text-[#0B192C] transition-colors" style={{ letterSpacing: "-0.01em" }}>
                          {item.q}
                        </span>
                        <span className="text-[#0B192C] flex-shrink-0 mt-1">
                          {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="pb-6 pr-10 text-[15px] text-neutral-600 leading-[1.85] sc-fade-up">
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 pt-16 border-t border-[#E5E5E5] flex flex-col md:flex-row gap-4">
          <Link to="/" className="sc-btn-primary inline-flex items-center gap-2">
            Entra nella waitlist
          </Link>
          <Link to="/manifesto" className="sc-btn-ghost inline-flex items-center gap-2">
            Leggi il manifesto
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
